import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiLoader } from 'react-icons/fi';
import Icon from '../Icon';
import { loadGoogleMapsScript } from '../../utils/googleMapsLoader';

export type ResolvedPakistanPlace = {
    lat: number;
    lng: number;
    city: string;
    state: string;
    area: string;
    postalCode: string;
    formattedAddress: string;
};

type EnrichedPrediction = {
    placeId: string;
    mainText: string;
    secondaryText: string;
    city: string;
    state: string;
    area: string;
    postalCode: string;
    formattedAddress: string;
    adrAddress: string;
    plusCode: string;
    vicinity: string;
    lat: number | null;
    lng: number | null;
};

/** Request as much as the legacy Places Details API allows (postal work / full address). */
const PLACE_DETAIL_FIELDS: string[] = [
    'address_components',
    'adr_address',
    'formatted_address',
    'geometry',
    'name',
    'place_id',
    'plus_code',
    'type',
    'url',
    'vicinity',
    'business_status',
    'utc_offset',
    'formatted_phone_number',
    'international_phone_number',
];

/**
 * The Google Maps JS typings can lag behind the runtime constants. In practice
 * `AutocompleteService.getPlacePredictions` may return statuses like
 * `INVALID_REQUEST` / `NOT_FOUND` which aren't always present in
 * `google.maps.places.PlacesServiceStatus` in older @types versions.
 */
type AutocompletePredictionsStatus =
    | 'INVALID_REQUEST'
    | 'NOT_FOUND'
    | 'OK'
    | 'OVER_QUERY_LIMIT'
    | 'REQUEST_DENIED'
    | 'UNKNOWN_ERROR'
    | 'ZERO_RESULTS';

function getPredictions(
    ac: google.maps.places.AutocompleteService,
    request: google.maps.places.AutocompletionRequest
): Promise<{
    predictions: google.maps.places.AutocompletePrediction[];
    status: AutocompletePredictionsStatus;
}> {
    return new Promise((resolve) => {
        ac.getPlacePredictions(request, (predictions, status) => {
            resolve({ predictions: predictions || [], status });
        });
    });
}

/** Merge several autocomplete calls (each returns up to 5) for broader Pakistan coverage. */
async function mergePakistanPredictions(
    ac: google.maps.places.AutocompleteService,
    input: string
): Promise<google.maps.places.AutocompletePrediction[]> {
    const base = { input, componentRestrictions: { country: 'pk' as const } };
    const bundles = await Promise.all([
        getPredictions(ac, { ...base }),
        getPredictions(ac, { ...base, types: ['geocode'] }),
        getPredictions(ac, { ...base, types: ['establishment'] }),
        getPredictions(ac, { ...base, types: ['(regions)'] }),
    ]);

    const seen = new Set<string>();
    const merged: google.maps.places.AutocompletePrediction[] = [];
    for (const { predictions, status } of bundles) {
        if (
            status !== google.maps.places.PlacesServiceStatus.OK &&
            status !== google.maps.places.PlacesServiceStatus.ZERO_RESULTS
        ) {
            continue;
        }
        for (const p of predictions) {
            const id = p.place_id;
            if (id && !seen.has(id)) {
                seen.add(id);
                merged.push(p);
            }
        }
    }
    return merged;
}

function pickComponent(
    components: google.maps.GeocoderAddressComponent[] | undefined,
    ...types: string[]
): string {
    if (!components?.length) return '';
    for (const t of types) {
        const c = components.find((x) => x.types.includes(t));
        if (c?.long_name) return c.long_name;
    }
    return '';
}

function parsePlaceResult(place: google.maps.places.PlaceResult): Omit<EnrichedPrediction, 'placeId' | 'mainText' | 'secondaryText'> {
    const comps = place.address_components || [];
    const city =
        pickComponent(comps, 'locality', 'administrative_area_level_2', 'postal_town') ||
        pickComponent(comps, 'administrative_area_level_3');
    const state = pickComponent(comps, 'administrative_area_level_1');
    const area =
        [
            pickComponent(comps, 'sublocality_level_1', 'sublocality'),
            pickComponent(comps, 'neighborhood'),
            pickComponent(comps, 'premise'),
            pickComponent(comps, 'route'),
        ]
            .filter(Boolean)
            .join(', ') || pickComponent(comps, 'sublocality');
    const postalCode = pickComponent(comps, 'postal_code');
    const loc = place.geometry?.location;
    const lat = loc ? loc.lat() : null;
    const lng = loc ? loc.lng() : null;
    const plus = place.plus_code
        ? `${place.plus_code.global_code || ''} ${place.plus_code.compound_code || ''}`.trim()
        : '';
    return {
        city,
        state,
        area,
        postalCode,
        formattedAddress: place.formatted_address || '',
        adrAddress: place.adr_address || '',
        plusCode: plus,
        vicinity: place.vicinity || '',
        lat,
        lng,
    };
}

type Props = {
    apiKey: string | undefined;
    onResolved: (place: ResolvedPakistanPlace) => void;
    className?: string;
};

const DEBOUNCE_MS = 350;
const MIN_CHARS = 2;
const MAX_ENRICHED = 15;

/** Wider than the input when narrow (e.g. grid cell in modals); capped for viewport */
const PANEL_MIN_WIDTH_PX = 480;
const PANEL_MAX_WIDTH_PX = 672;
const VIEWPORT_GUTTER_PX = 12;

function computePanelPosition(wrap: HTMLElement): { top: number; left: number; width: number } {
    const rect = wrap.getBoundingClientRect();
    const vw = window.innerWidth;
    const minW = Math.max(rect.width, PANEL_MIN_WIDTH_PX);
    let width = Math.min(PANEL_MAX_WIDTH_PX, Math.max(minW, vw - 2 * VIEWPORT_GUTTER_PX));
    let left = rect.left;
    if (left + width > vw - VIEWPORT_GUTTER_PX) {
        left = Math.max(VIEWPORT_GUTTER_PX, vw - VIEWPORT_GUTTER_PX - width);
    }
    return {
        top: rect.bottom + 4,
        left,
        width,
    };
}

export default function PakistanPlaceSearch({ apiKey, onResolved, className = '' }: Props) {
    const [input, setInput] = useState('');
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [items, setItems] = useState<EnrichedPrediction[]>([]);
    const [panelPos, setPanelPos] = useState<{ top: number; left: number; width: number } | null>(null);
    const wrapRef = useRef<HTMLDivElement>(null);
    const portalRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const updatePanelPosition = useCallback(() => {
        const el = wrapRef.current;
        if (!el) return;
        setPanelPos(computePanelPosition(el));
    }, []);

    const runSearch = useCallback(
        async (q: string) => {
            const key = apiKey?.trim();
            if (!key) {
                setError('Set REACT_APP_GOOGLE_MAPS_API_KEY in .env');
                setItems([]);
                setLoading(false);
                return;
            }
            if (q.length < MIN_CHARS) {
                setItems([]);
                setError(null);
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);
            setItems([]);
            try {
                await loadGoogleMapsScript(key);
                const ac = new google.maps.places.AutocompleteService();
                const merged = await mergePakistanPredictions(ac, q);
                const slice = merged.slice(0, MAX_ENRICHED);

                if (slice.length === 0) {
                    setItems([]);
                    setLoading(false);
                    return;
                }

                const svc = new google.maps.places.PlacesService(document.createElement('div'));
                const enriched: EnrichedPrediction[] = await Promise.all(
                    slice.map(
                        (p) =>
                            new Promise<EnrichedPrediction>((resolve) => {
                                svc.getDetails(
                                    {
                                        placeId: p.place_id!,
                                        fields: PLACE_DETAIL_FIELDS,
                                    },
                                    (place, st) => {
                                        if (st !== google.maps.places.PlacesServiceStatus.OK || !place) {
                                            resolve({
                                                placeId: p.place_id!,
                                                mainText: p.structured_formatting.main_text,
                                                secondaryText: p.structured_formatting.secondary_text || '',
                                                city: '',
                                                state: '',
                                                area: '',
                                                postalCode: '',
                                                formattedAddress: '',
                                                adrAddress: '',
                                                plusCode: '',
                                                vicinity: '',
                                                lat: null,
                                                lng: null,
                                            });
                                            return;
                                        }
                                        const parsed = parsePlaceResult(place);
                                        resolve({
                                            placeId: p.place_id!,
                                            mainText: p.structured_formatting.main_text,
                                            secondaryText: p.structured_formatting.secondary_text || '',
                                            ...parsed,
                                        });
                                    }
                                );
                            })
                    )
                );

                setItems(enriched);
            } catch (e: unknown) {
                setError(e instanceof Error ? e.message : 'Search failed');
                setItems([]);
            } finally {
                setLoading(false);
            }
        },
        [apiKey]
    );

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (input.trim().length < MIN_CHARS) {
            setItems([]);
            setError(null);
            setLoading(false);
            return;
        }
        debounceRef.current = setTimeout(() => {
            void runSearch(input.trim());
        }, DEBOUNCE_MS);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [input, runSearch]);

    useEffect(() => {
        const onDoc = (e: MouseEvent) => {
            const t = e.target as Node;
            if (wrapRef.current?.contains(t)) return;
            if (portalRef.current?.contains(t)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    const showPanel = open && (loading || error || items.length > 0 || input.trim().length >= MIN_CHARS);

    useLayoutEffect(() => {
        if (!showPanel) {
            setPanelPos(null);
            return;
        }
        updatePanelPosition();
    }, [showPanel, loading, error, items.length, input, updatePanelPosition]);

    useEffect(() => {
        if (!showPanel) return;
        const onScrollOrResize = () => updatePanelPosition();
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        return () => {
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
        };
    }, [showPanel, updatePanelPosition]);

    const handlePick = (row: EnrichedPrediction) => {
        if (row.lat == null || row.lng == null) {
            setError('Could not read coordinates for this place');
            return;
        }
        onResolved({
            lat: row.lat,
            lng: row.lng,
            city: row.city,
            state: row.state,
            area: row.area,
            postalCode: row.postalCode,
            formattedAddress: row.formattedAddress || [row.mainText, row.secondaryText].filter(Boolean).join(', '),
        });
        const pin = row.postalCode ? `PIN ${row.postalCode}` : row.mainText;
        setInput(pin);
        setOpen(false);
        setItems([]);
    };

    const panelClassName =
        'max-h-96 overflow-auto rounded-xl border border-gray-300 bg-white py-1 text-left text-sm shadow-lg dark:border-gray-600 dark:bg-gray-900';

    const panelBody = (
        <>
            {loading && (
                <div className="flex items-center gap-2 px-3 py-2 text-gray-500 dark:text-gray-400">
                    <Icon icon={FiLoader} className="h-4 w-4 shrink-0 animate-spin" />
                    <span>Searching…</span>
                </div>
            )}
            {!loading && error && <div className="px-3 py-2 text-red-600 dark:text-red-400">{error}</div>}
            {!loading && !error && items.length === 0 && input.trim().length >= MIN_CHARS && (
                <div className="px-3 py-2 text-gray-500 dark:text-gray-400">No matches</div>
            )}
            {!loading &&
                items.map((row) => (
                    <button
                        key={row.placeId}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handlePick(row)}
                        className="flex w-full flex-col gap-1 border-b border-gray-100 px-3 py-2.5 text-left last:border-0 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/80"
                    >
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                Postal code
                            </span>
                            <span className="font-mono text-base font-bold text-gray-900 dark:text-gray-100">
                                {row.postalCode || '—'}
                            </span>
                        </div>
                        {row.formattedAddress ? (
                            <p className="break-words text-xs leading-snug text-gray-800 dark:text-gray-200">
                                {row.formattedAddress}
                            </p>
                        ) : (
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{row.mainText}</p>
                        )}
                        <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-gray-600 dark:text-gray-400">
                            {row.city ? <span>City: {row.city}</span> : null}
                            {row.state ? <span>State: {row.state}</span> : null}
                            {row.area ? <span>Area: {row.area}</span> : null}
                        </div>
                        {row.plusCode ? (
                            <span className="text-xs text-gray-500 dark:text-gray-500">Plus code: {row.plusCode}</span>
                        ) : null}
                        {row.vicinity ? (
                            <span className="text-xs text-gray-500 dark:text-gray-500">{row.vicinity}</span>
                        ) : null}
                        {row.secondaryText && row.formattedAddress ? (
                            <span className="text-xs text-gray-500 dark:text-gray-500">{row.secondaryText}</span>
                        ) : null}
                    </button>
                ))}
        </>
    );

    const portalTarget = typeof document !== 'undefined' ? document.body : null;

    return (
        <div ref={wrapRef} className={`relative min-w-0 ${className}`}>
            <label className="form-field-label">Find location (Pakistan)</label>
            <input
                type="text"
                value={input}
                onChange={(e) => {
                    setInput(e.target.value);
                    setOpen(true);
                    setError(null);
                }}
                onFocus={() => setOpen(true)}
                placeholder="Postal code, city, street…"
                autoComplete="off"
                className="form-control text-sm"
            />
            {showPanel && panelPos && portalTarget
                ? createPortal(
                      <div
                          ref={portalRef}
                          className={`fixed z-[200] ${panelClassName}`}
                          style={{
                              top: panelPos.top,
                              left: panelPos.left,
                              width: panelPos.width,
                          }}
                          role="listbox"
                      >
                          {panelBody}
                      </div>,
                      portalTarget
                  )
                : null}
        </div>
    );
}
